import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import client from "@/Graphql/apolloClient";
import { EXPLORE_PROPERTY_BUTTON,GET_MARKETPLACE_COUNTRIES } from "@/Graphql/Queries";
import { Card, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, MapPin, Sparkles, Zap } from "lucide-react";
import { generateMockData } from "@/Graphql/MockData";
import { useAuth } from "@/utils/authContext";
import moment from "moment";
import { GET_LANDING_EXPLORATION } from "@/Graphql/PropertyListQueries";
import { useQuery } from "@apollo/client";
import { GET_MY_CART_ITEMS } from "@/Graphql/cartQueries";
import logger from '@/utils/logger';
import { fetchContentJson, extractPanelData, getFieldValue } from "@/utils/contentJsonParser";


interface FeaturedBillboardsProps {
  currencyCode: string;
  countryId: string;
  base_path: string;
  isExploreEnabled: boolean;
  isLoginEnabled: boolean;
  exposure: string;
}

export default function FeaturedBillboards({ currencyCode, countryId, base_path, isExploreEnabled, isLoginEnabled, exposure }: FeaturedBillboardsProps) {
  const navigate = useNavigate();
  // TODO: Replace with actual authentication logic
  const { isLoggedIn } = useAuth();
  const [exploreProperties, setExploreProperties] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [sectionTitle, setSectionTitle] = useState<string>("");
  const [sectionPrimaryHeader, setSectionPrimaryHeader] = useState<string>("");
  const [sectionSecondaryHeader, setSectionSecondaryHeader] = useState<string>("");
  const [sectionDescription, setSectionDescription] = useState<string>("");
  const [highImpactInventories, setHighImpactInventories] = useState<any[]>([]);
  const [cartCountryId, setCartCountryId] = useState<string>("");
  const [countriesMap, setCountriesMap] = useState<{ [key: string]: string }>({}); // Map country names to IDs

  const startDate = moment().format("DD MMMM YYYY");
  const endDate = moment().format("DD MMMM YYYY");

  const sessionDefaultCountryId = localStorage.getItem('sessionDefaultCountryId');
  
  // Fetch marketplace countries and build countries map
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const { data } = await client.query({
          query: GET_MARKETPLACE_COUNTRIES,
          variables: { exposure: "MARKETPLACE" },
          fetchPolicy: 'cache-first'
        });

     

        if (data?.getMarketplaceCountries && data.getMarketplaceCountries.length > 0) {
          // Create a map of country names to IDs
          const countryMap: { [key: string]: string } = {};
          data.getMarketplaceCountries.forEach((country: any) => {
            countryMap[country.name] = country.id;
          });

          setCountriesMap(countryMap);
         
        }
      } catch (error) {
        logger.error('❌ Error fetching marketplace countries:', error);
      }
    };

    fetchCountries();
  }, []);

  // Determine which countryId to use: cartCountryId if cart has items, otherwise sessionDefaultCountryId
  const selectedCountryID = localStorage.getItem('selectedCountryId');

  const effectiveCountryId = !isLoggedIn ? selectedCountryID : cartCountryId || sessionDefaultCountryId;

  const { data: staticHighImpactInventoryData, loading: staticHighImpactLoading } = useQuery(GET_LANDING_EXPLORATION, {
    fetchPolicy: 'network-only',
    variables: {
      "isCurated": true,
      "spotBasedBookingOnly": false,
      "billboard": "classic",
      "countryId": effectiveCountryId,
      "companyId": "",
      "dspName": exposure,
      startDate,
      endDate,
      "searchTerm": "",
      "sort": "purchaseFrequency,desc",
      "size": 5,
      "page": 0,
      "filterData": {
        "type": "CLASSIC",
        "availableBooking": true
      }
    },
    skip: !effectiveCountryId
  });

  // Fetch cart items data
  const fetchCartData = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const userId = localStorage.getItem('userId');

      if (!token || !userId) {
        
        return null;
      }

      const { data } = await client.query({
        query: GET_MY_CART_ITEMS,
        fetchPolicy: "network-only",
        variables: {
          accessToken: token,
          userId: userId,
        }
      });

      
      return data.cartpageItem || [];
    } catch (error) {
      return [];
    }
  };

  const { data: digitalHighImpactInventoryData, loading: digitalHighImpactLoading } = useQuery(GET_LANDING_EXPLORATION, {
    fetchPolicy: 'network-only',
    variables: {
      "isCurated": true,
      "spotBasedBookingOnly": false,
      "billboard": "digital",
      "countryId": effectiveCountryId,
      "companyId": "",
      "dspName": exposure,
      startDate,
      endDate,
      "searchTerm": "",
      "sort": "purchaseFrequency,desc",
      "size": 5,
      "page": 0,
      "filterData": {
        "type": "DIGITAL",
        "availableBooking": true
      }
    },
    skip: !effectiveCountryId
  });

  useEffect(() => {
    const classicInventories = (staticHighImpactInventoryData?.landingpageExplorePropertiesNotoken || []).map((item: any) => ({
      ...item,
      perDayPrice: typeof item.perDayPrice === 'number' ? item.perDayPrice : (typeof item.perDayPrice === 'string' ? parseFloat(item.perDayPrice.replace(/,/g, '')) : null),
      currencyCode: item.price?.localCurrencies?.code || item.currencyCode || currencyCode || 'N/A'
    }));
    
    const digitalInventories = (digitalHighImpactInventoryData?.landingpageExplorePropertiesNotoken || []).map((item: any) => ({
      ...item,
      perDayPrice: typeof item.perDayPrice === 'number' ? item.perDayPrice : (typeof item.perDayPrice === 'string' ? parseFloat(item.perDayPrice.replace(/,/g, '')) : null),
      currencyCode: item.price?.localCurrencies?.code || item.currencyCode || currencyCode || 'N/A'
    }));
    
    // Combine both classic and digital inventories
    const combinedInventories = [...classicInventories, ...digitalInventories];
    
    if (combinedInventories.length > 0) {
      setHighImpactInventories(combinedInventories);
    }
  }, [staticHighImpactInventoryData, digitalHighImpactInventoryData, currencyCode]);

  // Fetch cart data when component mounts or when user is authenticated
  useEffect(() => {
    const loadCartData = async () => {
      try {
        const cartItems = await fetchCartData();
        
        
        // Process cart response here
        if (cartItems && cartItems.length > 0) {
          
          
          // Extract country ID from first cart item - similar to login.tsx
          const firstCartItem = cartItems[0];
          
          
          let cartItemCountryId = '';
          
          // First, check if cartItemCountryId is already set (from login.tsx)
          if (firstCartItem?.cartItemCountryId) {
            cartItemCountryId = firstCartItem.cartItemCountryId;
           
          }
          
          // If not, try to extract country name and map it using countriesMap
          if (!cartItemCountryId && firstCartItem?.cartItemCountry) {
            const cartCountryName = firstCartItem.cartItemCountry;
            
            
            // Look up country ID using the countriesMap
            if (countriesMap && Object.keys(countriesMap).length > 0) {
              const foundCountryId = countriesMap[cartCountryName];
              if (foundCountryId) {
                cartItemCountryId = foundCountryId;
                
              } else {
                logger.warn(`⚠️ Country name "${cartCountryName}" not found in countries map`);
              }
            } else {
              logger.warn('⚠️ Countries map is not available yet');
            }
          }
          
          if (cartItemCountryId) {
            logger.log('✅ Setting cartCountryId from cart item:', cartItemCountryId);
            setCartCountryId(cartItemCountryId);
          }
          
          // You can use the cart data here - e.g., mark items as "in cart"
          // Update UI based on which packages are already in the cart
        } else {
          logger.log('Cart is empty, using sessionDefaultCountryId');
          setCartCountryId(''); // Reset to use sessionDefaultCountryId
        }
      } catch (error) {
        logger.error('Failed to load cart data:', error);
        setCartCountryId(''); // Reset on error to use sessionDefaultCountryId
      }
    };

    loadCartData();
  }, [countriesMap]); // Re-run when countriesMap is loaded

  // Set contentData from props when configData changes
  useEffect(() => {
    const fetchContent = async () => {
      try {
        const data = await fetchContentJson();
        
        // Extract the featured inventories panel
        const featuredPanel = extractPanelData(data, "featuredInventoriesPanel");
        
        if (featuredPanel) {
          setSectionTitle(getFieldValue(featuredPanel, "featured_billboard_title", ""));
          setSectionPrimaryHeader(getFieldValue(featuredPanel, "featured_primary_header", ""));
          setSectionSecondaryHeader(getFieldValue(featuredPanel, "featured_secondary_header", ""));
          setSectionDescription(getFieldValue(featuredPanel, "featured_description", ""));
        }

      } catch (error) {
        logger.error("Error fetching content:", error);
      }
    };

    fetchContent();
  }, []);

  // Fetch explore properties
  useEffect(() => {
    const fetchMockData = async () => {
      try {
        const mockResponse = await generateMockData(EXPLORE_PROPERTY_BUTTON);
        let mockData = mockResponse.data?.landingpageExploreButton;

        if (mockData && mockData.length > 0) {
          mockData = mockData.filter((item: any) => (item.billboardType || '').toLowerCase() !== 'classic-transit');
          const filteredData = mockData.filter((item: any) => {
            const type = (item.billboardType || item.type || '').toLowerCase().trim();
            return type === 'classic' || type === 'digital';
          });
          setExploreProperties(filteredData);
        } else {
          throw new Error('Mock data was empty or invalid');
        }
      } catch (error) {
        logger.error("Error fetching mock explore properties:", error);
        setExploreProperties([]); // Use fallback data when no IDs and not mock mode
        setLoading(false);

      } finally {
        setLoading(false);
      }
    };

    if (import.meta.env.VITE_APP_MOCK_DATA === "true") {
      setLoading(true);
      fetchMockData(); // Always fetch mock data when enabled
    } else if (highImpactInventories.length > 0) {
      setExploreProperties(highImpactInventories); // Fetch real data if selected IDs are present
    }
  }, [highImpactInventories]);


  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsPerView, setItemsPerView] = useState(3);

  useEffect(() => {
    const updateItemsPerView = () => {
      if (window.innerWidth < 768) {
        setItemsPerView(1);
      } else if (window.innerWidth < 1024) {
        setItemsPerView(2);
      } else {
        setItemsPerView(3);
      }
    };

    updateItemsPerView();
    window.addEventListener('resize', updateItemsPerView);
    return () => window.removeEventListener('resize', updateItemsPerView);
  }, []);

  useEffect(() => {
    if (exploreProperties.length > itemsPerView) {
      const timer = setInterval(() => {
        setCurrentIndex((prevIndex) => {
          const maxIndex = exploreProperties.length - itemsPerView;
          return prevIndex >= maxIndex ? 0 : prevIndex + 1;
        });
      }, 4000); // Faster transition - every 4 seconds
      return () => clearInterval(timer);
    } else {
      setCurrentIndex(0); // Always show first slide if not enough items
    }
  }, [itemsPerView, exploreProperties.length]);

  const nextSlide = () => {
    setCurrentIndex((prevIndex) =>
      (prevIndex + 1) % (exploreProperties.length - itemsPerView + 1)
    );
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? exploreProperties.length - itemsPerView : prevIndex - 1
    );
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Premium':
        return 'bg-primary-600 text-white';
      case 'Available':
        return 'bg-forest-custom text-white';
      case 'High Traffic':
        return 'bg-accent-amber text-white';
      default:
        return 'bg-gray-500 text-white';
    }
  };


  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 sm:mb-12 lg:mb-16">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-primary-100 to-forest-100 border border-primary-200 text-primary-700 px-6 py-3 rounded-full text-sm shadow-sm mb-6">
            <Sparkles className="h-4 w-4 text-primary-600" />
            <span className="text-lg sm:text-xl leading-relaxed">{sectionTitle}</span>
            <Zap className="h-4 w-4 text-forest-custom" />
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold leading-tight FontStyles mb-4 sm:mb-6">
            <span className="text-gray-900 block">{sectionPrimaryHeader}</span>
            <span className="bg-gradient-to-r from-primary-600 to-forest-custom text-green-600 bg-clip-text block">{sectionSecondaryHeader}</span>
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            {sectionDescription}
          </p>
        </div>

        <div className="relative overflow-hidden">
          {exploreProperties.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                  <Sparkles className="h-8 w-8 text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-600 mb-2">No Billboards Found</h3>
              </div>
            </div>
          ) : (
            <>
          {/* Auto-scrolling carousel */}
          <div
            className="flex transition-all duration-700 ease-in-out"
            style={{ transform: `translateX(-${currentIndex * (100 / itemsPerView)}%)` }}
          >
            {exploreProperties.map((billboard) => (
              <div
                key={billboard.id}
                className="w-full md:w-1/2 xl:w-1/3 flex-shrink-0 px-3 lg:px-4"
              >
                <Card className="card-hover border-0 shadow-lg overflow-hidden flex flex-col h-full group transition-all duration-500 hover:shadow-2xl hover:scale-[1.02]">
                  <div className="relative aspect-video w-full overflow-hidden">
                    <img
                      src={
                        (billboard.thumbnail &&
                          billboard.thumbnail !== 'null' &&
                          billboard.thumbnail.trim() !== '')
                          ? billboard.thumbnail
                          : (billboard.thumbnailPath &&
                            billboard.thumbnailPath !== 'null' &&
                            billboard.thumbnailPath.trim() !== '')
                            ? billboard.thumbnailPath
                            : `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`
                      }
                      alt={billboard.name || billboard.displayName || billboard.title}
                      className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-110"
                    />
                    {/* Animated overlay on hover */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    <Badge
                      className={`absolute top-3 right-3 text-xs font-medium px-2 py-1 ${getCategoryColor(billboard.category || billboard.billboardType)} transition-transform duration-300 group-hover:scale-110`}
                    >
                      {billboard.type || 'N/A'}
                    </Badge>
                    {/* Animated view indicator */}
                    <div className="absolute bottom-8 left-3 bg-white/95 backdrop-blur-sm rounded-lg px-3 py-1 transform translate-y-full group-hover:translate-y-0 transition-transform duration-500">
                      <div className="flex items-center gap-1 text-sm font-semibold text-gray-900">
                        <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                        {billboard.stateName || billboard.state?.name || 'N/A'}, {billboard.districtName || billboard.district?.name || 'N/A'}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col flex-grow p-4 sm:p-6">
                    <div className="mb-3">
                      <CardTitle
                        className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 line-clamp-1 group-hover:text-primary-600 transition-colors duration-300"
                        title={(billboard.name || billboard.displayName || billboard.title)?.length > 30 ? (billboard.name || billboard.displayName || billboard.title) : ""}
                      >
                        {(billboard.name || billboard.displayName || billboard.title)?.length > 30
                          ? `${(billboard.name || billboard.displayName || billboard.title).slice(0, 30)}...`
                          : (billboard.name || billboard.displayName || billboard.title)}
                      </CardTitle>
                      <div className="flex items-center text-gray-600 group-hover:text-gray-800 transition-colors duration-300">
                        <MapPin className="h-4 w-4 mr-2 flex-shrink-0 text-primary-600" />
                        <span className="text-sm sm:text-base truncate">{billboard.country?.name || billboard.state?.name || billboard.location || billboard.countryName || billboard.stateName || billboard.districtName}</span>
                      </div>
                    </div>
                    {/* {(billboard.publisher?.name || billboard.mediaOwnerName) && (() => {
                      const ownerName = billboard.publisher?.name || billboard.mediaOwnerName;
                      const mediaOwner = getMediaOwner(ownerName);
                      if (mediaOwner) {
                        return (
                          <div className="flex items-center space-x-2">
                            <img
                              src={mediaOwner.logo}
                              alt={mediaOwner.name}
                              className="w-5 h-5 rounded-full object-cover flex-shrink-0"
                            />
                            <span className="font-medium text-emerald-600 hover:text-emerald-700 cursor-pointer">{mediaOwner.name}</span>
                          </div>
                        );
                      }
                      return <span className="font-medium"> by {ownerName}</span>;
                    })() || <span className="font-medium"> by N/A</span>} */}
                    <div className="mt-auto">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center text-forest-custom font-bold text-lg sm:text-xl group-hover:scale-105 transition-transform duration-300">
                          <span className="whitespace-nowrap">
                            {typeof billboard.perDayPrice === 'number' && !isNaN(billboard.perDayPrice)
                              ? `${billboard.currencyCode || currencyCode || 'N/A'} ${billboard.perDayPrice?.toLocaleString()}`
                              : (typeof billboard.perDayPrice === 'string'
                                ? `${billboard.currencyCode || currencyCode || 'N/A'} ${parseFloat(billboard.perDayPrice.replace(/,/g, '')).toLocaleString()}`
                                : `${billboard.currencyCode || currencyCode || 'N/A'} N/A`)}
                          </span>
                          <span className="text-sm font-normal text-gray-600 ml-1">
                            /Day
                          </span>
                        </div>
                      </div>
                      <Button
                        className="w-full btn-primary-gradient hover:bg-primary-700 text-white font-medium py-2.5 transform hover:scale-[1.02] transition-all duration-200"
                        data-testid={`button-view-featured-${billboard.id}`}
                        onClick={() => {
                          const billboardType = billboard.type || billboard.billboardType;
                          if (!isLoggedIn) {
                            if (!isExploreEnabled && isLoggedIn) {
                              // Hide button before login if isExploreEnabled is false
                              return null;
                            }
                            if (!isLoginEnabled) {
                              // Show button before login, navigate to billboard/
                              navigate(`${base_path}/billboard/${billboard.id}?type=${encodeURIComponent(billboardType)}`, {
                                state: {
                                  currentPrice: billboard.perDayPrice,
                                  priceFormat: 'landing-page'
                                }
                              });
                              return;
                            }
                            // Default: navigate to billboard/
                            navigate(`${base_path}/billboard/${billboard.id}?type=${encodeURIComponent(billboardType)}`, {
                              state: {
                                currentPrice: billboard.perDayPrice,
                                priceFormat: 'landing-page'
                              }
                            });
                          } else {
                            if (!isExploreEnabled) {
                              // Show button after login, navigate to property-details
                              navigate(`${base_path}/property-details/${billboard.id}?type=${encodeURIComponent(billboardType)}`, {
                                state: {
                                  currentPrice: billboard.perDayPrice,
                                  startDate: moment().format("YYYY-MM-DD"),
                                  endDate: moment().endOf('month').format("YYYY-MM-DD"),
                                  priceFormat: 'landing-page'
                                }
                              });
                              return;
                            }
                            if (!isLoginEnabled) {
                              // Hide button after login
                              return null;
                            }
                            // Default: navigate to property-details
                            navigate(`${base_path}/property-details/${billboard.id}?type=${encodeURIComponent(billboardType)}`, {
                              state: {
                                currentPrice: billboard.perDayPrice,
                                startDate: moment().format("YYYY-MM-DD"),
                                endDate: moment().endOf('month').format("YYYY-MM-DD"),
                                priceFormat: 'landing-page'
                              }
                            });
                          }
                        }}
                        style={
                          (!isLoggedIn && !isExploreEnabled && isLoginEnabled) || (isLoggedIn && !isLoginEnabled) ? { display: 'none' } : {}
                        }
                      >
                        View Details
                      </Button>
                    </div>
                  </div>
                </Card>
              </div>
            ))}
          </div>

          {/* Enhanced progress indicators */}
          {exploreProperties.length > itemsPerView && (
          <div className="flex justify-center mt-8 space-x-3">
            {Array.from({ length: exploreProperties.length - itemsPerView + 1 }).map((_, index) => (
              <button
                key={index}
                className={`relative overflow-hidden rounded-full transition-all duration-500 ${index === currentIndex
                  ? 'w-8 h-3 bg-primary-600 shadow-lg shadow-primary-600/40'
                  : 'w-3 h-3 bg-gray-300 hover:bg-gray-400'
                  }`}
                onClick={() => setCurrentIndex(index)}
                data-testid={`indicator-${index}`}
              >
                {index === currentIndex && (
                  <div className="absolute inset-0 bg-gradient-to-r from-primary-500 to-emerald-500 animate-pulse"></div>
                )}
              </button>
            ))}
          </div>
        )}
      {/* Navigation Buttons - Visible and accessible */}
      {exploreProperties.length > itemsPerView && (
        <>
          <Button
            variant="outline"
            size="icon"
            onClick={prevSlide}
            className="absolute left-2 sm:left-4 top-1/2 transform -translate-y-1/2 bg-white shadow-xl border-2 border-primary-600 text-primary-600 hover:bg-primary-600 hover:text-white w-10 h-10 sm:w-12 sm:h-12 z-30 rounded-full transition-all duration-300 hover:scale-110"
            data-testid="button-carousel-prev"
          >
            <ChevronLeft className="h-5 w-5 sm:h-6 sm:w-6" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={nextSlide}
            className="absolute right-2 sm:right-4 top-1/2 transform -translate-y-1/2 bg-white shadow-xl border-2 border-primary-600 text-primary-600 hover:bg-primary-600 hover:text-white w-10 h-10 sm:w-12 sm:h-12 z-30 rounded-full transition-all duration-300 hover:scale-110"
            data-testid="button-carousel-next"
          >
            <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6" />
          </Button>
              </>
            )}
          </>
        )}
        </div>
      </div>
    </section>
  );
}
